<template>
  <div class="page-layout doctor-profile-page">
    <section class="doctor-profile-hero">
      <b-container>
        <b-button
          variant="link"
          class="doctor-profile-back px-0 mb-3 d-inline-flex align-items-center"
          @click="goBack"
        >
          <b-icon :icon="backIcon" class="doctor-profile-back__icon" />
          {{ langDec.doctor_profile_back }}
        </b-button>

        <div v-if="loading" class="doctor-profile-loading text-center py-5">
          <b-spinner variant="primary" />
          <p class="mt-3 text-muted mb-0">{{ langDec.doctor_profile_loading }}</p>
        </div>

        <div v-else-if="errorCode" class="doctor-profile-error text-center py-5">
          <p class="text-danger mb-3">{{ errorMessage }}</p>
          <b-button variant="primary" @click="fetchDoctor">{{ langDec.doctor_profile_retry }}</b-button>
        </div>

        <template v-else-if="doctor">
          <b-row class="doctor-profile-layout">
            <b-col lg="4" md="5" cols="12" class="mb-4 mb-lg-0 doctor-profile-sidebar-col">
              <div class="doctor-profile-card doctor-profile-card--photo">
                <div class="doctor-profile-photo-wrap">
                  <img
                    class="doctor-profile-photo"
                    :src="photoUrl"
                    :alt="displayName"
                  />
                </div>
                <div class="doctor-profile-rating" v-if="hasRating">
                  <span class="doctor-profile-rating__stars" aria-hidden="true">★</span>
                  <span>{{ formatRating(doctor.rating) }}</span>
                  <span class="doctor-profile-rating__count text-muted">({{ doctor.ratesCount || 0 }} {{ langDec.doctor_profile_ratings_word }})</span>
                </div>
                <div v-if="doctor.hourRate" class="doctor-profile-rate">
                  <span class="doctor-profile-rate__label">{{ langDec.doctor_profile_hourly_rate }}</span>
                  <span class="doctor-profile-rate__value">{{ doctor.hourRate }} {{ langDec.doctor_profile_currency_egp }}</span>
                </div>
              </div>
            </b-col>

            <b-col lg="8" md="7" cols="12">
              <div class="doctor-profile-card doctor-profile-card--main">
                <h1 class="doctor-profile-name">{{ displayName }}</h1>
                <p v-if="headline" class="doctor-profile-headline">{{ headline }}</p>
                <p v-if="educationLine" class="doctor-profile-edu text-muted">{{ educationLine }}</p>
                <p v-if="doctor.universityCountryName" class="doctor-profile-uni text-muted small">
                  {{ doctor.universityCountryName }}
                </p>

                <div v-if="doctor.bio" class="doctor-profile-bio">
                  <h2 class="doctor-profile-section-title">{{ langDec.doctor_profile_about }}</h2>
                  <p class="doctor-profile-bio-text">{{ doctor.bio }}</p>
                </div>

                <div v-if="locationLine" class="doctor-profile-meta-line">
                  <b-icon icon="geo-alt" class="doctor-profile-meta-icon" />
                  {{ locationLine }}
                </div>

                <div v-if="doctor.phoneNumber" class="doctor-profile-meta-line">
                  <b-icon icon="telephone" class="doctor-profile-meta-icon" />
                  <a :href="'tel:' + stripTel(doctor.phoneNumber)" class="doctor-profile-link">{{ doctor.phoneNumber }}</a>
                </div>
                <div v-if="doctor.email" class="doctor-profile-meta-line">
                  <b-icon icon="envelope" class="doctor-profile-meta-icon" />
                  <a :href="'mailto:' + doctor.email" class="doctor-profile-link">{{ doctor.email }}</a>
                </div>
                <div v-if="formattedDob" class="doctor-profile-meta-line text-muted small">
                  {{ langDec.doctor_profile_dob }}: {{ formattedDob }}
                </div>
              </div>

              <div v-if="tagItems('specializations').length" class="doctor-profile-card mt-3">
                <h2 class="doctor-profile-section-title">{{ langDec.doctor_profile_specializations }}</h2>
                <div class="doctor-profile-tags">
                  <b-badge
                    v-for="(tag, i) in tagItems('specializations')"
                    :key="'sp-' + i"
                    variant="primary"
                    pill
                    class="doctor-profile-tag"
                  >{{ tag }}</b-badge>
                </div>
              </div>

              <div v-if="tagItems('jobDegrees').length" class="doctor-profile-card mt-3">
                <h2 class="doctor-profile-section-title">{{ langDec.doctor_profile_job_title }}</h2>
                <div class="doctor-profile-tags">
                  <b-badge
                    v-for="(tag, i) in tagItems('jobDegrees')"
                    :key="'job-' + i"
                    variant="secondary"
                    pill
                    class="doctor-profile-tag"
                  >{{ tag }}</b-badge>
                </div>
              </div>

              <div v-if="tagItems('degrees').length" class="doctor-profile-card mt-3">
                <h2 class="doctor-profile-section-title">{{ langDec.doctor_profile_academic_degree }}</h2>
                <div class="doctor-profile-tags">
                  <b-badge
                    v-for="(tag, i) in tagItems('degrees')"
                    :key="'deg-' + i"
                    variant="info"
                    pill
                    class="doctor-profile-tag doctor-profile-tag--dark"
                  >{{ tag }}</b-badge>
                </div>
              </div>

              <div v-if="interestNames.length" class="doctor-profile-card mt-3">
                <h2 class="doctor-profile-section-title">{{ langDec.doctor_profile_interests }}</h2>
                <div class="doctor-profile-tags doctor-profile-tags--wrap">
                  <b-badge
                    v-for="(name, i) in interestNames"
                    :key="'int-' + i"
                    variant="light"
                    class="doctor-profile-tag doctor-profile-tag--interest"
                  >{{ name }}</b-badge>
                </div>
              </div>
            </b-col>
          </b-row>
        </template>
      </b-container>
    </section>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { sendRequest } from "../utils/RequestHelper.js";

export default {
  name: "DoctorProfile",
  data() {
    return {
      doctor: null,
      loading: true,
      errorCode: null,
    };
  },
  computed: {
    ...mapGetters(["getLangData", "getlang"]),
    langDec() {
      return this.getLangData || {};
    },
    isAr() {
      return (this.getlang || "ar") === "ar";
    },
    backIcon() {
      return this.isAr ? "arrow-right" : "arrow-left";
    },
    errorMessage() {
      const k = this.errorCode;
      const d = this.langDec || {};
      const map = {
        invalid_id: "doctor_profile_err_invalid_id",
        not_found: "doctor_profile_err_not_found",
        not_in_list: "doctor_profile_err_not_in_list",
        network: "doctor_profile_err_network",
      };
      const prop = map[k];
      return prop ? d[prop] || "" : "";
    },
    displayName() {
      if (!this.doctor) return "";
      const f = this.doctor.firstName || "";
      const l = this.doctor.lastName || "";
      const fb = this.langDec.doctor_profile_role_fallback || "";
      return `${f} ${l}`.trim() || this.doctor.userName || fb;
    },
    headline() {
      const d = this.doctor;
      if (!d) return "";
      const jobs = d.jobDegrees || [];
      const specs = d.specializations || [];
      const mid = this.isAr
        ? ` ${this.langDec.doctor_profile_headline_in || "في"} `
        : " — ";
      if (jobs.length && specs.length) {
        return `${jobs[0].name}${mid}${specs[0].name}`;
      }
      if (specs.length) return specs[0].name;
      if (jobs.length) return jobs[0].name;
      return "";
    },
    educationLine() {
      const d = this.doctor;
      const dec = this.langDec || {};
      if (!d || !d.scientificDegrees || !d.scientificDegrees.length) return "";
      let s = `${dec.experts_degree_prefix || ""} ${d.scientificDegrees[0].name}`.trim();
      if (d.universityName) {
        s += ` ${dec.experts_degree_from || ""} ${d.universityName}`;
      }
      return s.trim();
    },
    locationLine() {
      const d = this.doctor;
      if (!d) return "";
      const parts = [d.city, d.country].filter(Boolean);
      const sep = this.isAr ? "، " : ", ";
      return parts.join(sep);
    },
    photoUrl() {
      const d = this.doctor;
      if (!d || !d.imageUrl) {
        return (
          "data:image/svg+xml," +
          encodeURIComponent(
            '<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200"><circle fill="#e8f5ef" cx="100" cy="100" r="100"/><text x="100" y="110" text-anchor="middle" fill="#006633" font-size="48">👤</text></svg>'
          )
        );
      }
      const path = d.imageUrl;
      if (/^https?:\/\//i.test(path)) return path;
      const base = (process.env.VUE_APP_API_URL_IMAGE || "").replace(/\/?$/, "/");
      return base + String(path).replace(/^\//, "");
    },
    interestNames() {
      const list = (this.doctor && this.doctor.interests) || [];
      return list.map((x) => x.interestName || x.name || "").filter(Boolean);
    },
    hasRating() {
      const d = this.doctor;
      return d && Number(d.rating) > 0;
    },
    formattedDob() {
      const raw = this.doctor && this.doctor.dateOfBirth;
      if (!raw) return "";
      const dt = new Date(raw);
      if (Number.isNaN(dt.getTime())) return String(raw);
      const loc = this.isAr ? "ar-EG" : "en-US";
      return dt.toLocaleDateString(loc, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },
  },
  watch: {
    "$route.params.id"() {
      this.fetchDoctor();
    },
  },
  created() {
    this.fetchDoctor();
  },
  methods: {
    tagItems(kind) {
      const d = this.doctor;
      if (!d) return [];
      if (kind === "specializations") {
        return (d.specializations || []).map((x) => x.name).filter(Boolean);
      }
      if (kind === "jobDegrees") {
        return (d.jobDegrees || []).map((x) => x.name).filter(Boolean);
      }
      if (kind === "degrees") {
        return (d.scientificDegrees || []).map((x) => x.name).filter(Boolean);
      }
      return [];
    },
    formatRating(r) {
      const n = Number(r);
      if (Number.isNaN(n)) return "—";
      return n.toFixed(1);
    },
    stripTel(s) {
      return String(s || "").replace(/\s/g, "");
    },
    goBack() {
      if (window.history.length > 1) {
        this.$router.go(-1);
      } else {
        this.$router.push({ name: "experts" });
      }
    },
    applyDoctorPayload(raw) {
      if (!raw || typeof raw !== "object") return null;
      if (raw.id || raw.firstName) return raw;
      if (raw.data && typeof raw.data === "object") {
        if (raw.data.items && Array.isArray(raw.data.items) && raw.data.items[0]) {
          return raw.data.items[0];
        }
        return raw.data;
      }
      return null;
    },
    fetchDoctor() {
      const id = this.$route.params.id;
      if (!id) {
        this.loading = false;
        this.errorCode = "invalid_id";
        return;
      }
      this.loading = true;
      this.errorCode = null;
      this.doctor = null;

      const onSuccess = (response) => {
        const body = response && response.data;
        const doc =
          this.applyDoctorPayload(body) ||
          (body &&
            body.data &&
            body.data.items &&
            body.data.items.find((x) => String(x.id) === String(id))) ||
          null;
        if (doc) {
          this.doctor = doc;
          this.loading = false;
          return;
        }
        this.loading = false;
        this.errorCode = "not_found";
      };

      const onFail = () => {
        this.fallbackPostList(id);
      };

      sendRequest("get", `Doctors/${id}`, true, null, onSuccess, onFail);
    },
    fallbackPostList(id) {
      const body = {
        userRole: "Provider",
        pageNumber: 1,
        pageSize: 200,
      };
      sendRequest(
        "post",
        "Doctors/",
        true,
        body,
        (response) => {
          const items =
            (response.data && response.data.data && response.data.data.items) || [];
          const doc = items.find((x) => String(x.id) === String(id));
          if (doc) {
            this.doctor = doc;
            this.errorCode = null;
          } else {
            this.errorCode = "not_in_list";
          }
          this.loading = false;
        },
        () => {
          this.loading = false;
          this.errorCode = "network";
        }
      );
    },
  },
};
</script>

<style scoped>
.doctor-profile-page {
  padding-bottom: 3rem;
  background: #f4f7f5;
}

.doctor-profile-hero {
  padding: 1.5rem 0 2.5rem;
}

.doctor-profile-back {
  color: #006633 !important;
  font-weight: 700;
  text-decoration: none !important;
}

.doctor-profile-layout {
  margin-top: 0.25rem;
}

/* عمود الصورة + السعر: sticky تحت الـ navbar — الصف بدون align-items-start حتى يمتد العمود بارتفاع المحتوى الطويل (نفس فكرة بطاقة الاستشارات الجانبية) */
@media (min-width: 768px) {
  .doctor-profile-sidebar-col .doctor-profile-card--photo {
    position: sticky;
    top: var(--app-sticky-top, calc(var(--app-navbar-offset, 5.35rem) + 1rem));
    z-index: 2;
  }
}

.doctor-profile-card {
  background: #fff;
  border-radius: 16px;
  padding: 1.5rem;
  border: 1px solid rgba(0, 102, 51, 0.1);
  box-shadow: 0 10px 36px rgba(0, 102, 51, 0.07);
}

.doctor-profile-card--photo {
  text-align: center;
}

.doctor-profile-photo-wrap {
  display: flex;
  justify-content: center;
  margin-bottom: 1rem;
}

.doctor-profile-photo {
  width: 180px;
  height: 180px;
  object-fit: cover;
  border-radius: 50%;
  border: 4px solid rgba(0, 102, 51, 0.15);
  box-shadow: 0 8px 28px rgba(0, 102, 51, 0.12);
}

.doctor-profile-rating {
  font-weight: 700;
  color: #b8860b;
  margin-bottom: 0.75rem;
}

.doctor-profile-rating__stars {
  margin-inline-end: 0.25rem;
}

.doctor-profile-back__icon {
  flex-shrink: 0;
  margin-inline-end: 0.35rem;
}

.doctor-profile-meta-line {
  display: flex;
  align-items: flex-start;
  gap: 0.35rem;
}

.doctor-profile-meta-icon {
  flex-shrink: 0;
  margin-top: 0.15rem;
}

.doctor-profile-card--main {
  text-align: start;
}

.doctor-profile-rating__count {
  font-weight: 500;
  font-size: 0.85rem;
}

.doctor-profile-rate {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(0, 102, 51, 0.1);
}

.doctor-profile-rate__label {
  font-size: 0.75rem;
  color: #6c757d;
}

.doctor-profile-rate__value {
  font-size: 1.25rem;
  font-weight: 800;
  color: #006633;
}

.doctor-profile-name {
  font-size: clamp(1.35rem, 3vw, 1.85rem);
  font-weight: 800;
  color: #1a1d1f;
  margin: 0 0 0.5rem;
  line-height: 1.3;
}

.doctor-profile-headline {
  font-size: 1.1rem;
  font-weight: 700;
  color: #006633;
  margin-bottom: 0.35rem;
}

.doctor-profile-edu {
  margin-bottom: 0.25rem;
}

.doctor-profile-uni {
  margin-bottom: 1rem;
}

.doctor-profile-section-title {
  font-size: 1rem;
  font-weight: 800;
  color: #006633;
  margin: 0 0 0.75rem;
  padding-bottom: 0.35rem;
  border-bottom: 2px solid rgba(0, 102, 51, 0.15);
}

.doctor-profile-bio-text {
  margin: 0;
  line-height: 1.85;
  color: #3d4548;
  white-space: pre-wrap;
}

.doctor-profile-meta-line {
  margin-top: 0.6rem;
  font-size: 0.95rem;
}

.doctor-profile-link {
  color: #006633;
  font-weight: 600;
}

.doctor-profile-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
}

.doctor-profile-tags--wrap {
  gap: 0.4rem;
}

.doctor-profile-tag {
  font-weight: 600 !important;
  padding: 0.4em 0.75em !important;
  font-size: 0.85rem !important;
}

.doctor-profile-tag--dark {
  background: #0d3d26 !important;
  color: #fff !important;
}

.doctor-profile-tag--interest {
  border: 1px solid rgba(0, 102, 51, 0.2) !important;
  color: #2d3436 !important;
  background: #f6faf7 !important;
}

html[data-theme="dark"] .doctor-profile-page {
  background: #0e1117;
}

html[data-theme="dark"] .doctor-profile-card {
  background: #161b22;
  border-color: #30363d;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);
}

html[data-theme="dark"] .doctor-profile-name {
  color: #f0f3f6;
}

html[data-theme="dark"] .doctor-profile-bio-text {
  color: #cfd6df;
}

html[data-theme="dark"] .doctor-profile-tag--interest {
  background: #21262d !important;
  border-color: #30363d !important;
  color: #e8eaed !important;
}

html[data-theme="dark"] .doctor-profile-back {
  color: #8fe9c0 !important;
}

html[data-theme="dark"] .doctor-profile-link {
  color: #8fe9c0;
}
</style>
